import { createContext, useState, useEffect } from 'react';

export const CartContext = createContext();

// A "cart line" is uniquely identified by product id + combinationId (if any).
// Two different combinations of the same product (e.g. Black/M and Red/L) are
// separate cart lines, but a plain product with no variants keeps working
// exactly as before — its cartLineId just falls back to the plain id.
function buildCartLineId(id, combinationId) {
  return combinationId ? `${id}-${combinationId}` : `${id}`;
}

export const CartProvider = ({ children }) => {
    const [cartItems, setCartItems] = useState(() => {
        const storedItems = localStorage.getItem('cartItems');
        return storedItems ? JSON.parse(storedItems) : [];
    });

    useEffect(() => {
        localStorage.setItem('cartItems', JSON.stringify(cartItems));
    }, [cartItems]);

    // product may optionally include combinationId + combinationText when it
    // was picked via the variant picker. Both are omitted/undefined for
    // plain products, preserving the old behavior exactly.
    // We also store stockQuantity and sellIfNotProvidedInStore from the product
    // so Cart can enforce quantity limits on restricted items.
    const addToCart = (product) => {
    setCartItems(prevItems => {
      const cartLineId = buildCartLineId(product.id, product.combinationId);
      const existingItem = prevItems.find(item => item.cartLineId === cartLineId);

      // Ensure stock info is always set with safe defaults
      const stockQuantity = typeof product.stockQuantity === 'number' ? product.stockQuantity : null;
      const sellIfNotProvidedInStore = product.sellIfNotProvidedInStore === true;

      if (existingItem) {
        // When incrementing, check if we would exceed stock limit
        let newQuantity = existingItem.quantity + 1;

        // For restricted items with valid stock info, don't exceed available stock
        if (
          typeof existingItem.stockQuantity === 'number' &&
          existingItem.stockQuantity > 0 &&
          existingItem.sellIfNotProvidedInStore === false
        ) {
          newQuantity = Math.min(newQuantity, existingItem.stockQuantity);
        }

        return prevItems.map(item =>
          item.cartLineId === cartLineId
            ? {
                ...item,
                quantity: newQuantity,
                // Update stock info in case it changed
                stockQuantity: stockQuantity ?? item.stockQuantity,
                sellIfNotProvidedInStore: sellIfNotProvidedInStore ?? item.sellIfNotProvidedInStore
              }
            : item
        );
      }

      return [...prevItems, {
        ...product,
        cartLineId,
        quantity: 1,
        stockQuantity,
        sellIfNotProvidedInStore
      }];
    });
  };

    const clearCart = () => {setCartItems([]);
      localStorage.removeItem('cart');}

      const removeFromCart = (cartLineId) => {
    setCartItems(prev => prev.filter(item => item.cartLineId !== cartLineId));
  };

   const updateQuantity = (cartLineId, quantity) => {
    setCartItems(prev =>
      prev.map(item => {
        if (item.cartLineId !== cartLineId) return item;

        // Ensure quantity is a valid positive number
        let finalQuantity = Math.max(Math.floor(quantity), 1);

        // Enforce stock limit for restricted items (sellIfNotProvidedInStore = false)
        // Only if we have valid stock info
        if (
          typeof item.stockQuantity === 'number' &&
          item.stockQuantity > 0 &&
          item.sellIfNotProvidedInStore === false
        ) {
          finalQuantity = Math.min(finalQuantity, item.stockQuantity);
        }

        return { ...item, quantity: finalQuantity };
      })
    );
  };

    return (
    <CartContext.Provider value={{ cartItems, addToCart, clearCart, removeFromCart, updateQuantity }}>
      {children}
    </CartContext.Provider>
  );
};